import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  CircularProgress,
  Chip,
  Tooltip,
  Grid,
} from '@mui/material';
import { clinicPageContainerSx } from '../../hooks/useClinicPageBreakpoints';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import StarIcon from '@mui/icons-material/Star';
import BusinessIcon from '@mui/icons-material/Business';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import { useAuth } from '../../contexts/AuthContext';

interface UnitRow {
  id: string;
  name: string;
  short_name: string;
  cnpj: string | null;
  email: string | null;
  phone: string | null;
  city: string | null;
  state: string | null;
  is_matrix: boolean;
  is_active: boolean;
  sort_order: number;
}

const emptyForm: Partial<UnitRow> & { is_active: boolean } = {
  name: '',
  short_name: '',
  cnpj: '',
  email: '',
  phone: '',
  city: '',
  state: '',
  is_active: true,
  sort_order: 0,
};

const Units: React.FC = () => {
  const { isAdmin, refreshUser } = useAuth();
  const [rows, setRows] = useState<UnitRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<typeof emptyForm>({ ...emptyForm });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(getApiUrl('/units'), { params: { include_inactive: true } });
      const data = res.data?.success !== false ? res.data?.data ?? [] : [];
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const sortedRows = useMemo(() => {
    return rows
      .slice()
      .sort((a, b) => {
        if (a.is_matrix && !b.is_matrix) return -1;
        if (!a.is_matrix && b.is_matrix) return 1;
        return (a.sort_order || 0) - (b.sort_order || 0) || a.name.localeCompare(b.name);
      });
  }, [rows]);

  const openCreate = () => {
    setEditId(null);
    setForm({
      ...emptyForm,
      sort_order: (rows.reduce((m, r) => Math.max(m, r.sort_order || 0), 0) || 0) + 10,
    });
    setDlgOpen(true);
  };

  const openEdit = (r: UnitRow) => {
    setEditId(r.id);
    setForm({
      name: r.name,
      short_name: r.short_name,
      cnpj: r.cnpj || '',
      email: r.email || '',
      phone: r.phone || '',
      city: r.city || '',
      state: r.state || '',
      is_active: r.is_active,
      sort_order: r.sort_order || 0,
    });
    setDlgOpen(true);
  };

  const save = async () => {
    const name = (form.name || '').trim();
    const short = (form.short_name || '').trim();
    if (!name || !short) {
      alert('Nome e nome curto são obrigatórios.');
      return;
    }
    setSaving(true);
    try {
      if (editId) {
        await axios.put(getApiUrl(`/units/${editId}`), form);
      } else {
        await axios.post(getApiUrl('/units'), form);
      }
      setDlgOpen(false);
      await load();
      // Atualiza available_units no contexto (caso o admin agora tenha + unidades)
      try { await refreshUser(); } catch (_e) { /* ignore */ }
    } catch (e: any) {
      console.error(e);
      alert(e.response?.data?.error || 'Erro ao salvar unidade.');
    } finally {
      setSaving(false);
    }
  };

  const removeUnit = async (r: UnitRow) => {
    if (r.is_matrix) {
      alert('A unidade matriz não pode ser excluída.');
      return;
    }
    if (!window.confirm(`Excluir a unidade "${r.name}"? Essa ação não pode ser desfeita.`)) return;
    try {
      await axios.delete(getApiUrl(`/units/${r.id}`));
      await load();
      try { await refreshUser(); } catch (_e) { /* ignore */ }
    } catch (e: any) {
      alert(e.response?.data?.error || 'Erro ao excluir unidade.');
    }
  };

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ color: '#dc2626' }}>
          Acesso restrito a administradores.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={clinicPageContainerSx}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a' }}>
            Unidades / Filiais
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
            Gerencie as unidades da clínica. Há sempre uma matriz; usuários precisam de pelo menos uma unidade vinculada.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreate}
          sx={{ bgcolor: '#14b8a6', '&:hover': { bgcolor: '#0d9488' }, textTransform: 'none' }}
        >
          Nova unidade
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 1px 12px rgba(0,0,0,0.06)' }}>
        {loading ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <CircularProgress sx={{ color: '#14b8a6' }} />
          </Box>
        ) : (
          <Table size="small">
            <TableHead sx={{ bgcolor: '#f0fdf4' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, color: '#0d9488' }}>Nome</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#0d9488' }}>Curto</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#0d9488' }}>Cidade</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#0d9488' }}>CNPJ</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#0d9488' }}>Status</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: '#0d9488' }}>
                  Ações
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: '#94a3b8' }}>
                    Nenhuma unidade cadastrada
                  </TableCell>
                </TableRow>
              ) : (
                sortedRows.map((r) => (
                  <TableRow key={r.id} hover sx={{ opacity: r.is_active ? 1 : 0.7 }}>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BusinessIcon sx={{ fontSize: 16, color: '#0d9488' }} />
                        {r.name}
                        {r.is_matrix && (
                          <Chip
                            label="Matriz"
                            size="small"
                            icon={<StarIcon sx={{ fontSize: 14 }} />}
                            sx={{ bgcolor: '#fef9c3', color: '#854d0e', height: 20, fontSize: '0.65rem' }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{r.short_name}</TableCell>
                    <TableCell>{[r.city, r.state].filter(Boolean).join('/')}</TableCell>
                    <TableCell>{r.cnpj || '—'}</TableCell>
                    <TableCell>
                      {r.is_active ? (
                        <Chip label="Ativa" size="small" sx={{ bgcolor: '#dcfce7', color: '#166534', fontWeight: 600 }} />
                      ) : (
                        <Chip label="Inativa" size="small" sx={{ bgcolor: '#f1f5f9', color: '#64748b', fontWeight: 600 }} />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => openEdit(r)} sx={{ color: '#0d9488' }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={r.is_matrix ? 'A matriz não pode ser excluída' : 'Excluir'}>
                        <span>
                          <IconButton size="small" disabled={r.is_matrix} onClick={() => removeUnit(r)} sx={{ color: '#dc2626' }}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      <Dialog open={dlgOpen} onClose={() => !saving && setDlgOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editId ? 'Editar unidade' : 'Nova unidade'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={8}>
              <TextField fullWidth label="Nome *" value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} margin="dense" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Nome curto *" value={form.short_name || ''} onChange={(e) => setForm({ ...form, short_name: e.target.value.toUpperCase() })} margin="dense" helperText="Ex: SP, MATRIZ, BH-SUL" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="CNPJ" value={form.cnpj || ''} onChange={(e) => setForm({ ...form, cnpj: e.target.value })} margin="dense" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Telefone" value={form.phone || ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} margin="dense" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="E-mail" value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} margin="dense" />
            </Grid>
            <Grid item xs={12} sm={9}>
              <TextField fullWidth label="Cidade" value={form.city || ''} onChange={(e) => setForm({ ...form, city: e.target.value })} margin="dense" />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField fullWidth label="UF" value={form.state || ''} onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase().slice(0, 2) })} margin="dense" inputProps={{ maxLength: 2 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Ordem" type="number" value={form.sort_order ?? 0} onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value, 10) || 0 })} margin="dense" />
            </Grid>
            <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <FormControlLabel control={<Switch checked={!!form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} color="success" />} label={form.is_active ? 'Ativa' : 'Inativa'} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDlgOpen(false)} disabled={saving}>Cancelar</Button>
          <Button variant="contained" disabled={saving || !form.name?.trim() || !form.short_name?.trim()} onClick={() => save()} sx={{ bgcolor: '#14b8a6' }}>
            {saving ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Units;
